import React, { useContext, useMemo } from "react"

import { noop } from "shared/lib/noop"
import { ProductConfigurationFormData } from "shared/types/ProductConfiguration"
import { RJSFSchema } from "shared/types/RJSF"

import { ProductConfiguratorProps } from "../ProductConfigurator"

export interface ProductConfiguratorConfigurationContextType {
  schema?: RJSFSchema | null
  formData?: ProductConfigurationFormData | null
  onFormDataChange: (
    formData: ProductConfigurationFormData,
    fieldID?: string
  ) => void | Promise<unknown>
  variantKey?: string
  errorMessages?: string[] | null
  isReady?: boolean
  isLoading?: boolean
  isUpdating?: boolean
  isDisabled?: boolean
  isSubmitting?: boolean
}

export const DEFAULT_PRODUCT_CONFIGURATOR_CONFIGURATION_CONTEXT: ProductConfiguratorConfigurationContextType =
  {
    onFormDataChange: noop
  }

export const ProductConfiguratorConfigurationContext =
  React.createContext<ProductConfiguratorConfigurationContextType>(
    DEFAULT_PRODUCT_CONFIGURATOR_CONFIGURATION_CONTEXT
  )

export function useProductConfiguratorConfigurationContext(): ProductConfiguratorConfigurationContextType {
  return useContext(ProductConfiguratorConfigurationContext)
}

export function useProductConfiguratorConfigurationContextValue(
  model: ProductConfiguratorProps
): ProductConfiguratorConfigurationContextType {
  const {
    schema,
    formData,
    onFormDataChange,
    variantKey,
    errorMessages,
    isReady,
    isLoading,
    isUpdating,
    isDisabled,
    isSubmitting
  } = model

  return useMemo<ProductConfiguratorConfigurationContextType>(
    () => ({
      schema,
      formData,
      onFormDataChange,
      variantKey,
      errorMessages,
      isReady,
      isLoading,
      isUpdating,
      isDisabled,
      isSubmitting
    }),
    [
      schema,
      formData,
      onFormDataChange,
      variantKey,
      errorMessages,
      isReady,
      isLoading,
      isUpdating,
      isDisabled,
      isSubmitting
    ]
  )
}

export function ProductConfiguratorConfigurationContextProvider({
  children,
  model
}: {
  children: React.ReactNode
  model: ProductConfiguratorProps
}) {
  const value = useProductConfiguratorConfigurationContextValue(model)

  return (
    <ProductConfiguratorConfigurationContext.Provider value={value}>
      {children}
    </ProductConfiguratorConfigurationContext.Provider>
  )
}
